/**
 * Frame-rate-independent exponential decay toward a target — simpler than a
 * full velocity-tracking spring-damper (no overshoot-clamping logic to get
 * wrong) for the spec's qualitative ask (§15-17): smooth, a slight lag,
 * never an instant snap.
 */
export function dampTowardTarget(current: number, target: number, deltaMs: number, smoothingTimeSec: number): number {
  if (smoothingTimeSec <= 0) return target;
  const factor = 1 - Math.exp(-(deltaMs / 1000) / smoothingTimeSec);
  return current + (target - current) * factor;
}

const GROUND_MARGIN_PX = 60;
/** The tower top settles here, as a fraction of viewport height from the bottom — spec §15's "55-65% from bottom", picked at the middle of that range. */
const ANCHOR_FROM_BOTTOM_FRACTION = 0.6;

/**
 * The camera's target vertical offset so the tower top settles at a
 * comfortable screen position once it's tall enough (spec §15): the camera
 * stays put (offset 0, ground near the bottom margin) until the
 * unpanned tower top would rise above the anchor line, then pans up just
 * enough to hold the top pinned there instead of letting it climb further
 * off-screen. World Y and screen px share a 1:1 scale for v1 — a
 * `StackTowerConfig.blockHeight` unit already doubles as a screen-pixel
 * unit, since nothing else needs a different scale yet.
 */
export function cameraOffsetForTowerTop(towerTopWorldY: number, viewportHeight: number): number {
  const anchorFromBottom = viewportHeight * ANCHOR_FROM_BOTTOM_FRACTION;
  const unpannedTopFromBottom = GROUND_MARGIN_PX + towerTopWorldY;
  return Math.max(0, unpannedTopFromBottom - anchorFromBottom);
}

/** Converts a world Y (0 = ground, increasing upward as the tower climbs) to a screen Y (0 = top of viewport, increasing downward) under the current camera offset. */
export function worldToScreenY(worldY: number, cameraOffset: number, viewportHeight: number): number {
  return viewportHeight - GROUND_MARGIN_PX - worldY + cameraOffset;
}
